// The import card in the kadmon panel — T83 (22 Sep 2026), agents doc § 7.6 item 6.
//
// WHAT IT IS. The recruiter attached a list to their question; kadmon read it with the import's own
// preview and said what it holds. This is that preview, under her answer: what would be imported,
// what is already on file, what the import will not take and why — and one Import.
//
// THE TAP RUNS THE IMPORT'S OWN CALLS, from this tab, on the ROWS the preview gave — the dialog's
// own confirm body, which its route re-reads row by row. The file's bytes are not sent again; they
// went up once, with the question.
//
// IT IS NOT ONE OF HER CONFIRM CARDS, and that is the point: a card is stored and shared, and a
// preview holds a file's rows, so storing it would write the file a second time — which T83 says it
// must not (services/bff/src/attachment.ts). So this card is drawn from the find's RESPONSE and
// belongs to the tab that asked: a colleague opening the chat reads her answer and sees no card,
// a reload loses an unconfirmed preview exactly as the dialog does, and attaching again is one tap.
//
// PEOPLE TAKE THREE CALLS, COMPANIES TWO — T43's and T63's own shapes, unchanged. A people import
// files one request per row through T38's door and this polls the settle route as the worker
// applies them; a company import finishes in its confirm, because `clients` and `client_contacts`
// are the brain's own to write.
//
// A LINKEDIN NETWORK SENDS ITS FILE AGAIN — T111 (23 Sep 2026). Its confirm is the Connections
// tab's own: the service plans the whole network against the brain at the tap, so what goes up is
// the file this tab still holds, not rows — a preview names only the first two hundred of fifteen
// thousand. Name matches stay held back here, as the card says; deciding them one by one is the
// tab's own import. So is filing a colleague's network (T111a): the card files the asker's own,
// and says so, because the file replaces whichever network it is filed under.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ConnectionsImportResult, ImportFiled, ImportProposal, SettledRow } from '@vera/bff';
import {
  IMPORT_POLL_MS,
  IMPORT_POLL_TIMEOUT_MS,
  useConfirmImport,
  useSettleImport,
} from '../api/import.ts';
import { useConfirmClientImport } from '../api/importClients.ts';
import { useConfirmConnectionsImport } from '../api/connections.ts';
import {
  addsNew,
  closeMatches,
  companyRows,
  importCardLines,
  importCardTitle,
  importReady,
  peopleRows,
  refusedRows,
  settlements,
  type AskAttachment,
} from '../importChat.ts';
import { Badge, Button } from '../ui/atoms.tsx';
import { WriteError } from '../ui/forms.tsx';

type Stage = 'proposed' | 'importing' | 'done';

export function ImportCard({
  proposal,
  file,
}: {
  proposal: ImportProposal;
  /** T111: the file the question carried — a LinkedIn network's Import sends it again. */
  file: AskAttachment | null;
}) {
  const confirmPeople = useConfirmImport();
  const confirmCompanies = useConfirmClientImport();
  const confirmConnections = useConfirmConnectionsImport();
  const settle = useSettleImport();

  const [stage, setStage] = useState<Stage>('proposed');
  const [failure, setFailure] = useState<unknown>(null);
  /** T43's three-call lane: what was filed, and what the worker has done with each. */
  const [filed, setFiled] = useState<ImportFiled[]>([]);
  const [settled, setSettled] = useState<Map<string, SettledRow>>(new Map());
  const [timedOut, setTimedOut] = useState(false);
  /** T63's two-call lane: what became of every row, straight from the confirm. */
  const [companies, setCompanies] = useState<{ created: number; updated: number } | null>(null);
  /** T111's lane: what the upload did, straight from the confirm. */
  const [network, setNetwork] = useState<ConnectionsImportResult | null>(null);

  const lines = importCardLines(proposal);
  const refused = refusedRows(proposal);
  const close = closeMatches(proposal);
  const ready = importReady(proposal);
  // A network's Import needs the file itself; a card drawn without it (a remount that lost it)
  // says so rather than offering a button that would send nothing.
  const fileMissing = proposal.kind === 'connections' && file === null;

  const rows = proposal.kind === 'people' ? peopleRows(proposal.preview) : [];

  const run = async () => {
    setFailure(null);
    setStage('importing');
    try {
      if (proposal.kind === 'people') {
        const outcome = await confirmPeople.mutateAsync({ file: proposal.file, rows });
        setFiled(outcome.requests);
        if (outcome.requests.length === 0) setStage('done');
      } else if (proposal.kind === 'connections') {
        if (!file) return;
        setNetwork(await confirmConnections.mutateAsync({ ...file, asNew: [] }));
        setStage('done');
      } else {
        const outcome = await confirmCompanies.mutateAsync({
          status: proposal.status,
          rows: companyRows(proposal.preview),
        });
        setCompanies({
          created: outcome.created.clients,
          updated: outcome.updated.clients,
        });
        setStage('done');
      }
    } catch (error) {
      setFailure(error);
      setStage('proposed');
    }
  };

  // The settle poll — THE DIALOG'S OWN SHAPE (screens/ImportCandidates.tsx): one tick per effect
  // run, scheduled on a timer, and the effect re-runs when an answer lands. Not a loop inside the
  // tick: the first build called `tick()` immediately on every re-run, so each answer started the
  // next request with no delay and a file of thirty requests hammered the route.
  //
  // It is not a timeout on the IMPORT either. Every request is filed and the worker applies them
  // whenever it next sweeps; what gives up is the spinner, which is what the foot then says.
  const settleRef = useRef(settle);
  settleRef.current = settle;
  const started = useRef(0);
  useEffect(() => {
    if (stage !== 'importing' || proposal.kind !== 'people' || filed.length === 0) return;
    if (started.current === 0) started.current = Date.now();
    let cancelled = false;
    const tick = async () => {
      const waiting = filed.filter((request) => {
        const status = settled.get(request.entryId)?.status;
        return status !== 'applied' && status !== 'refused';
      });
      if (waiting.length === 0) {
        setStage('done');
        return;
      }
      try {
        const answered = await settleRef.current.mutateAsync(settlements(waiting, rows));
        if (cancelled) return;
        setSettled((was) => {
          const next = new Map(was);
          for (const row of answered) next.set(row.entryId, row);
          return next;
        });
      } catch {
        // Retried on the next tick; the card says it is still being applied either way.
      }
      if (Date.now() - started.current > IMPORT_POLL_TIMEOUT_MS) {
        setTimedOut(true);
        setStage('done');
      }
    };
    const timer = setTimeout(() => void tick(), settled.size === 0 ? 300 : IMPORT_POLL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // Re-running on every settled change is the poll; the mutation object behind the ref is stable.
  }, [stage, filed, settled, proposal.kind]);

  const applied = [...settled.values()].filter((row) => row.status === 'applied').length;
  const refusedByWorker = [...settled.values()].filter((row) => row.status === 'refused');
  const busy = stage === 'importing';
  const status: { label: string; tone: 'info' | 'ok' | 'warn' } = busy
    ? {
        label:
          proposal.kind === 'people' ? `Importing… ${applied} of ${filed.length}` : 'Importing…',
        tone: 'info',
      }
    : stage === 'done'
      ? { label: 'Imported', tone: 'ok' }
      : { label: 'Waiting on you', tone: 'warn' };

  return (
    <div
      className={`proposal proposal--import proposal--${stage === 'done' ? 'applied' : 'proposed'}`}
    >
      <div className="proposal__head">
        <span>
          {importCardTitle(proposal)} · {proposal.file}
        </span>
        <Badge tone={status.tone} dot={busy}>
          {status.label}
        </Badge>
      </div>
      <ul className="proposal__steps">
        {lines.map((line) => (
          <li key={line} className="proposal__step">
            <span className="proposal__mark" aria-hidden="true">
              ·
            </span>
            <span>{line}</span>
          </li>
        ))}
      </ul>
      {close.length > 0 && (
        <p className="micro proposal__note">
          {proposal.kind === 'connections'
            ? `Held back, a name matching somebody on file: ${close.join(' · ')}. To add any of them as somebody else, use Import on the Connections tab instead.`
            : `Matched to what is already on file: ${close.join(' · ')}. To decide those one by one, use Import on the ${proposal.kind === 'people' ? 'Candidates' : 'CRM'} screen instead.`}
        </p>
      )}
      {addsNew(proposal) && stage === 'proposed' && (
        <p className="micro proposal__note">
          {`Anything new here is added when you import. If one is already in the brain under another name, use Import on the ${proposal.kind === 'people' ? 'Candidates' : 'CRM'} screen instead: it lets you pick the one that exists.`}
        </p>
      )}
      {proposal.kind === 'connections' && stage === 'proposed' && (
        <p className="micro proposal__note">
          Filed as your own network. If the file is a colleague’s, use Import on the Connections tab
          instead. It asks whose network it is.
        </p>
      )}
      {refused.length > 0 && (
        <div className="proposal__refused">
          <p className="micro proposal__note">
            {refused.length === 1
              ? 'This row will not be imported'
              : `These ${refused.length} rows will not be imported`}
            :
          </p>
          <ul className="proposal__steps">
            {refused.map((row) => (
              <li key={row.key} className="proposal__step proposal__step--skipped">
                <span className="proposal__mark" aria-hidden="true">
                  !
                </span>
                <span>
                  {row.where}
                  {row.name ? ` · ${row.name}` : ''}
                  <span className="proposal__detail"> · {row.reason}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {stage === 'done' && proposal.kind === 'people' && (
        <p className="micro proposal__note">
          {applied} on file{refusedByWorker.length > 0 ? `, ${refusedByWorker.length} refused` : ''}
          {timedOut ? ' · the rest are filed and will land on their own' : ''} ·{' '}
          <Link to="/candidates">Open Candidates</Link>
        </p>
      )}
      {stage === 'done' && companies && (
        <p className="micro proposal__note">
          {companies.created} created, {companies.updated} updated ·{' '}
          <Link to="/crm">Open the CRM</Link>
        </p>
      )}
      {stage === 'done' && network && (
        <p className="micro proposal__note">
          {network.added} added, {network.updated} refreshed
          {network.removed > 0 ? `, ${network.removed} removed` : ''} ·{' '}
          <Link to="/crm/connections">Open Connections</Link>
        </p>
      )}
      <div className="proposal__foot">
        <span className="micro row-meta">
          {stage !== 'proposed'
            ? 'The import ran as you.'
            : ready === 0
              ? // Every row refused: there is no Import to offer, so the foot must not point at one.
                'There is nothing here to import. Every row was refused. Fix the file and attach it again.'
              : fileMissing
                ? 'The file is no longer in this tab. Attach it again, or use Import on the Connections tab.'
                : 'Nothing is imported until you tap Import. The file stays in this tab, and a reload loses the card.'}
        </span>
        {stage === 'proposed' && ready > 0 && !fileMissing && (
          <div className="proposal__buttons">
            <Button kind="accent" size="sm" icon="check" disabled={busy} onClick={() => void run()}>
              {proposal.kind === 'companies'
                ? `Import ${ready} ${ready === 1 ? 'company' : 'companies'}`
                : `Import ${ready} ${ready === 1 ? 'person' : 'people'}`}
            </Button>
          </div>
        )}
      </div>
      {failure !== null && (
        <div style={{ padding: '0 14px 10px' }}>
          <WriteError error={failure} what="run the import" />
        </div>
      )}
    </div>
  );
}
